import { TFunction } from "i18next";
import { ChatType } from "@/server/types/chat";
import { OA_BOT_USERNAME } from "@/config/constants";

/**
 * Resolves the user-facing typing label for a chat from the raw list of typing
 * usernames. Returns `null` when nothing should be shown.
 *
 * Identity model: Orion has NO display-name field — the username IS the only
 * identity shown anywhere (the Member type carries only `username`; P2P chat
 * names are the peer's username). So group labels use the typing event's
 * username verbatim — no resolution, no member-cache lookup, no network fetch,
 * fully synchronous.
 *
 * Priority order (the OA bot is checked FIRST, before any chat-type logic):
 *  1. Self is filtered out — you never see your own typing.
 *  2. OA bot (`OA_BOT_USERNAME`, env-derived from CHAT_HUB_ID — e.g. `theoa` in
 *     prod/beta/staging, `oa-agent` in stage-om) among typers -> "Responding..."
 *     (typing.responding).
 *     Decision (Codex #7): bot typing takes precedence and SUPPRESSES concurrent
 *     human typers. Rationale: the user is waiting on the bot's answer, so
 *     "Responding..." is the signal that matters. In practice the bot only types
 *     in OA chats where it is effectively the sole responder. If a future product
 *     call wants mixed output ("Responding... · Ana is typing"), change it here —
 *     but make it a conscious choice, not an accident.
 *  3. P2P chat, someone else typing -> "typing..." (typing.single).
 *  4. Group, 1 typer  -> "{name} is typing..." (typing.singleNamed).
 *  5. Group, 2 typers -> "{a} and {b} are typing..." (typing.twoNamed).
 *  6. Group, 3+ typers -> "Several people are typing..." (typing.several).
 *
 * @param chatType        ChatType of the active chat.
 * @param typingUsernames Raw usernames currently typing (may include self).
 * @param t               react-i18next translation function.
 * @param selfUsername    The current user's username (excluded from the label).
 */
export const resolveTypingLabel = (
  chatType: ChatType,
  typingUsernames: string[],
  t: TFunction,
  selfUsername: string,
): string | null => {
  const others = typingUsernames.filter((u) => u && u !== selfUsername);
  if (others.length === 0) {
    return null;
  }

  // Special case FIRST — the OA bot, wherever it types. Checked before any
  // chat-type test so it works in P2P or group, and suppresses humans.
  if (others.includes(OA_BOT_USERNAME)) {
    return t("typing.responding");
  }

  if (chatType === ChatType.P2P) {
    return t("typing.single");
  }

  // Group: the username IS the display identity — used directly, no lookup.
  if (others.length === 1) {
    return t("typing.singleNamed", { name: others[0] });
  }
  if (others.length === 2) {
    return t("typing.twoNamed", { a: others[0], b: others[1] });
  }
  return t("typing.several");
};
